import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { LimitGauge, PositionGauge, SectorGauge } from '@/api/types';
import { RiskChip } from '@/components/RiskChip';
import { cn } from '@/lib/cn';
import { ago, krw, pct } from '@/lib/format';

const SEV: Record<string, { tone: string; bg: string; ring: string; icon: typeof CheckCircle2; label: string }> = {
  OK:     { tone: 'text-kam-green', bg: 'bg-kam-green-soft border-kam-green/25', ring: 'bg-kam-green',  icon: CheckCircle2, label: 'OK' },
  WARN:   { tone: 'text-kam-amber', bg: 'bg-kam-amber-soft border-kam-amber/30', ring: 'bg-kam-amber',  icon: AlertTriangle, label: 'Warn' },
  BREACH: { tone: 'text-kam-red',   bg: 'bg-kam-red-soft border-kam-red/25',     ring: 'bg-kam-red',    icon: XCircle,       label: 'Breach' },
};

export function RiskPosture() {
  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['riskPosture'],
    queryFn: dashboardApi.riskPosture,
    refetchInterval: 15_000,
  });

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <ShieldAlert size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Risk Posture</h1>
          <div className="text-xs text-text-faint">
            {isLoading ? 'loading…'
              : isError ? 'unable to reach /api/risk/posture'
              : `Live exposure vs limits · refreshed ${ago(new Date(dataUpdatedAt).toISOString())}`}
          </div>
        </div>
      </div>

      {data && (
        <div className="space-y-6 max-w-5xl">
          {/* Headline */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="NAV" value={krw(data.nav)} />
            <Tile label="Holdings" value={krw(data.holdingsValue)} />
            <Tile label="Cash" value={krw(data.cash)} subtle="approx" />
            <Tile label="Positions" value={String(data.holdingsCount)} />
          </div>

          {/* Portfolio-wide limits */}
          <Section title="Portfolio Limits">
            <div className="grid gap-2 md:grid-cols-3">
              {data.limits.map((g) => <LimitCard key={g.key} g={g} />)}
            </div>
          </Section>

          {/* Per-position concentration */}
          <Section title={`Position Concentration · cap ${pct(data.positions[0]?.limitPct ?? 0, { sign: false })}`}>
            {data.positions.length === 0
              ? <Empty>No open positions.</Empty>
              : <div className="card card-tight overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-text-faint text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left font-medium py-1.5 px-2">Code</th>
                        <th className="text-left font-medium py-1.5 px-2">Name</th>
                        <th className="text-right font-medium py-1.5 px-2">Weight</th>
                        <th className="text-left font-medium py-1.5 pl-2 pr-1 w-[36%]">vs Limit</th>
                        <th className="text-right font-medium py-1.5 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.positions.map((p) => <PositionRow key={p.code} p={p} />)}
                    </tbody>
                  </table>
                </div>}
          </Section>

          {/* Per-sector concentration */}
          <Section title={`Sector Concentration · cap ${pct(data.sectors[0]?.limitPct ?? 0, { sign: false })}`}>
            {data.sectors.length === 0
              ? <Empty>No sector data yet.</Empty>
              : <div className="card card-tight overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-text-faint text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left font-medium py-1.5 px-2">Sector</th>
                        <th className="text-right font-medium py-1.5 px-2">Weight</th>
                        <th className="text-left font-medium py-1.5 pl-2 pr-1 w-[40%]">vs Limit</th>
                        <th className="text-right font-medium py-1.5 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sectors.map((s) => <SectorRow key={s.sector} s={s} />)}
                    </tbody>
                  </table>
                </div>}
          </Section>

          {/* Active risk conditions */}
          <Section title={`Active Risk Conditions (${data.activeConditions.length})`}>
            {data.activeConditions.length === 0
              ? <Empty>No active risk conditions configured.</Empty>
              : <div className="flex flex-wrap gap-2">
                  {data.activeConditions.map((c) => <RiskChip key={c.id} chip={c} />)}
                </div>}
          </Section>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, subtle }: { label: string; value: string; subtle?: string }) {
  return (
    <div className="card card-tight">
      <div className="text-xs text-text-faint uppercase tracking-wide mb-1">
        {label}{subtle && <span className="ml-1 opacity-60">({subtle})</span>}
      </div>
      <div className="text-base font-semibold font-mono tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-sm font-semibold mb-2 text-text-dim">{title}</h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="card card-tight text-xs text-text-faint italic">{children}</div>;
}

function LimitCard({ g }: { g: LimitGauge }) {
  const cfg = SEV[g.severity] ?? SEV.OK;
  const Icon = cfg.icon;
  const valueStr = g.actualValue == null
    ? `limit ${formatVal(g.limitValue, g.unit)}`
    : `${formatVal(g.actualValue, g.unit)} / ${formatVal(g.limitValue, g.unit)}`;

  return (
    <div className={cn('card card-tight border', cfg.bg)}>
      <div className="flex items-start gap-2 mb-2">
        <Icon size={16} className={cn('mt-0.5 flex-none', cfg.tone)} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold">{g.label}</div>
          <div className="text-xs text-text-dim font-mono tabular-nums truncate">{valueStr}</div>
        </div>
      </div>
      {g.pctOfLimit != null && <ProgressBar pct={g.pctOfLimit} sev={g.severity} />}
    </div>
  );
}

function PositionRow({ p }: { p: PositionGauge }) {
  const sev = SEV[p.severity] ?? SEV.OK;
  const pctOfCap = p.limitPct > 0 ? (p.weightPct / p.limitPct) * 100 : 0;
  return (
    <tr className="border-t border-[color:var(--border)]">
      <td className="py-1.5 px-2 font-mono text-xs">{p.code}</td>
      <td className="py-1.5 px-2 truncate max-w-[160px]">{p.name}</td>
      <td className="py-1.5 px-2 text-right font-mono tabular-nums">{pct(p.weightPct, { sign: false })}</td>
      <td className="py-1.5 pl-2 pr-1"><ProgressBar pct={pctOfCap} sev={p.severity} /></td>
      <td className={cn('py-1.5 px-2 text-right text-xs font-semibold', sev.tone)}>{sev.label}</td>
    </tr>
  );
}

function SectorRow({ s }: { s: SectorGauge }) {
  const sev = SEV[s.severity] ?? SEV.OK;
  const pctOfCap = s.limitPct > 0 ? (s.weightPct / s.limitPct) * 100 : 0;
  return (
    <tr className="border-t border-[color:var(--border)]">
      <td className="py-1.5 px-2 truncate">{s.sector}</td>
      <td className="py-1.5 px-2 text-right font-mono tabular-nums">{pct(s.weightPct, { sign: false })}</td>
      <td className="py-1.5 pl-2 pr-1"><ProgressBar pct={pctOfCap} sev={s.severity} /></td>
      <td className={cn('py-1.5 px-2 text-right text-xs font-semibold', sev.tone)}>{sev.label}</td>
    </tr>
  );
}

function ProgressBar({ pct, sev }: { pct: number; sev: string }) {
  const cfg = SEV[sev] ?? SEV.OK;
  const clamped = Math.min(Math.max(pct, 0), 120);
  const widthPct = (clamped / 120) * 100;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-surface-2 rounded-full overflow-hidden relative">
        <div className={cn('h-full', cfg.ring)} style={{ width: `${widthPct}%` }} />
        {/* 100% tick */}
        <div className="absolute top-0 bottom-0 w-px bg-text-faint/40" style={{ left: `${(100 / 120) * 100}%` }} />
      </div>
      <span className="text-xs font-mono tabular-nums text-text-faint w-12 text-right">
        {clamped.toFixed(0)}%
      </span>
    </div>
  );
}

function formatVal(v: number, unit: string): string {
  if (unit === '%') return pct(v, { sign: false });
  if (unit === 'KRW') return krw(v);
  return v.toFixed(0);
}
